import { Tag, VStack } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { PickingState } from "flows/Picking/models/order/types";
import { getEmployeeName, getPickingStateConfig } from "flows/Picking/utils/pickingDetailsHelpers";
import { IntlMessageId } from "shared/lang";
import { Button } from "ui/Button/Button";
import { Modal, ModalProps } from "ui/Modal/Modal";
import { BodyS, HeaderS, TitleXS } from "ui/Typography/Typography";

import { TableLine } from "./TableLine";

export type PickingConflictData = {
  pickingState: string | null;
  startedBy: { id: string; displayName: string | null } | null;
  finishedBy: { id: string; displayName: string | null } | null;
  handoverDetails: {
    containerIds: string[];
    shelfNumbers: string[];
  } | null;
};

function getConflictMessages(pickingState: string | null): {
  titleId: IntlMessageId;
  descriptionId: IntlMessageId;
} {
  switch (pickingState) {
    case PickingState.PICKING_STATE_IN_PROGRESS:
      return {
        titleId: "components.picking.conflict-modal.title.in_progress",
        descriptionId: "components.picking.conflict-modal.description.in_progress",
      };
    case PickingState.PICKING_STATE_COMPLETED:
      return {
        titleId: "components.picking.conflict-modal.title.completed",
        descriptionId: "components.picking.conflict-modal.description.completed",
      };
    case PickingState.PICKING_STATE_CANCELLED:
      return {
        titleId: "components.picking.conflict-modal.title.cancelled",
        descriptionId: "components.picking.conflict-modal.description.cancelled",
      };
    default:
      return {
        titleId: "components.picking.conflict-modal.title.default",
        descriptionId: "components.picking.conflict-modal.description.default",
      };
  }
}

type PickingConflictModalProps = {
  conflictData: PickingConflictData;
  onClose: () => void;
  onProceed: () => void;
  hideProceed?: boolean;
} & Pick<ModalProps, "isOpen">;

export function PickingConflictModal({
  isOpen = false,
  conflictData,
  onClose,
  onProceed,
  hideProceed: hideProceedOverride = false,
}: PickingConflictModalProps) {
  const intl = useIntl();
  const { pickingState, startedBy, finishedBy, handoverDetails } = conflictData;
  const stateConfig = pickingState ? getPickingStateConfig(pickingState) : null;
  const isCancelled = pickingState === PickingState.PICKING_STATE_CANCELLED;
  const hideProceed = hideProceedOverride || isCancelled;
  const { titleId, descriptionId } = getConflictMessages(pickingState);

  return (
    <Modal
      trapFocus={false}
      closeOnOverlayClick={false}
      isCentered
      isOpen={isOpen}
      onClose={onClose}
    >
      <Modal.Content marginX="s300">
        <Modal.Header textAlign="center" paddingX="s200">
          <HeaderS>
            <FormattedMessage id={titleId} />
          </HeaderS>
        </Modal.Header>
        <Modal.Body paddingY="0" paddingX="s200">
          <VStack spacing="s200" w="100%">
            <BodyS textAlign="center" color="grey.700">
              <FormattedMessage id={descriptionId} />
            </BodyS>

            <VStack spacing="0" w="100%">
              {stateConfig && (
                <TableLine
                  label={intl.formatMessage({
                    id: "components.picking.order-details.picking-state",
                  })}
                  rightElem={
                    <Tag bg={stateConfig.bg} borderRadius="4px">
                      <TitleXS color="white">
                        {intl.formatMessage({ id: stateConfig.messageId })}
                      </TitleXS>
                    </Tag>
                  }
                />
              )}

              {startedBy && (
                <TableLine
                  label={intl.formatMessage({
                    id: "components.picking.order-details.started-by",
                  })}
                  rightElem={getEmployeeName(startedBy)}
                />
              )}

              {finishedBy && (
                <TableLine
                  label={intl.formatMessage({
                    id: "components.picking.order-details.finished-by",
                  })}
                  rightElem={getEmployeeName(finishedBy)}
                />
              )}

              {handoverDetails?.containerIds && handoverDetails.containerIds.length > 0 && (
                <TableLine
                  label={intl.formatMessage({
                    id: "components.picking.order-details.containers",
                  })}
                  height="fit-content"
                  rightElem={
                    <BodyS my="s200" width="50%" align="end" wordBreak="break-word">
                      {handoverDetails.containerIds.map((value) => value.slice(-5)).join(", ")}
                    </BodyS>
                  }
                />
              )}

              {handoverDetails?.shelfNumbers && handoverDetails.shelfNumbers.length > 0 && (
                <TableLine
                  label={intl.formatMessage({
                    id: "components.picking.order-details.shelves",
                  })}
                  rightElem={
                    <BodyS my="s200" width="50%" align="end" wordBreak="break-word">
                      {handoverDetails.shelfNumbers.join(", ")}
                    </BodyS>
                  }
                />
              )}
            </VStack>
          </VStack>
        </Modal.Body>
        <Modal.Footer padding="s200" flexDirection="column" gap="s100">
          <Button onClick={onClose} isFullWidth size="lg" data-testid="conflict-close-btn">
            <FormattedMessage id="components.picking.conflict-modal.close" />
          </Button>
          {!hideProceed && (
            <Button
              onClick={onProceed}
              isFullWidth
              size="lg"
              variant="secondary"
              data-testid="conflict-proceed-btn"
            >
              <FormattedMessage id="components.picking.conflict-modal.proceed" />
            </Button>
          )}
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
}
